import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  TextField,
  Typography,
} from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import { Autocomplete } from '@react-google-maps/api';
import {
  DuplicateVenueError,
  findDuplicateVenue,
  PlaceVenueDraft,
  placeToVenueDraft,
  VenueDuplicateCandidate,
} from '../../utils/venueFromGooglePlace';

const AUTOCOMPLETE_OPTIONS: google.maps.places.AutocompleteOptions = {
  types: ['establishment'],
  componentRestrictions: { country: 'au' },
  fields: [
    'place_id',
    'name',
    'formatted_address',
    'geometry',
    'formatted_phone_number',
    'international_phone_number',
    'website',
    'types',
    'opening_hours',
    'address_components',
  ],
};

interface AddVenueFromPlacesDialogProps {
  open: boolean;
  mapsLoaded: boolean;
  saving: boolean;
  existingVenues: VenueDuplicateCandidate[];
  onClose: () => void;
  onConfirm: (draft: PlaceVenueDraft) => Promise<void>;
}

const AddVenueFromPlacesDialog: React.FC<AddVenueFromPlacesDialogProps> = ({
  open,
  mapsLoaded,
  saving,
  existingVenues,
  onClose,
  onConfirm,
}) => {
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [draft, setDraft] = useState<PlaceVenueDraft | null>(null);
  const [error, setError] = useState('');
  const ignoreInputChange = useRef(false);

  useEffect(() => {
    if (open) {
      setInputKey(k => k + 1);
      setDraft(null);
      setError('');
      setAutocomplete(null);
      ignoreInputChange.current = false;
    }
  }, [open]);

  const applyDraft = useCallback((nextDraft: PlaceVenueDraft) => {
    setDraft(nextDraft);
    const existing = findDuplicateVenue(existingVenues, nextDraft);
    if (existing) {
      const existingName = existing.businessName || existing.name || nextDraft.name;
      setError(`"${existingName}" is already in the venues list.`);
      return;
    }
    setError('');
  }, [existingVenues]);

  const handlePlaceChanged = useCallback(() => {
    if (!autocomplete) return;
    ignoreInputChange.current = true;
    try {
      const place = autocomplete.getPlace();
      applyDraft(placeToVenueDraft(place));
    } catch (err: any) {
      setDraft(null);
      setError(err?.message || 'Could not read that place. Try another result.');
    }
  }, [applyDraft, autocomplete]);

  const handleInputChange = useCallback(() => {
    if (ignoreInputChange.current) {
      ignoreInputChange.current = false;
      return;
    }
    setDraft(null);
    setError('');
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!draft || saving || error) return;
    try {
      await onConfirm(draft);
    } catch (err: any) {
      const message = err instanceof DuplicateVenueError
        ? err.message
        : (err?.message || 'Failed to add venue.');
      setError(message);
    }
  }, [draft, error, onConfirm, saving]);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      disableEnforceFocus
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <GlobalStyles styles={{ '.pac-container': { zIndex: 2000 } }} />
      <DialogTitle>Add venue from Google Places</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search by venue name, then confirm to add it as a managed venue.
        </Typography>

        {!mapsLoaded ? (
          <Box display="flex" alignItems="center" gap={1} py={2}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Loading Google Places…</Typography>
          </Box>
        ) : (
          <Autocomplete
            onLoad={setAutocomplete}
            onPlaceChanged={handlePlaceChanged}
            options={AUTOCOMPLETE_OPTIONS}
          >
            <TextField
              key={inputKey}
              fullWidth
              autoFocus
              label="Venue name"
              placeholder="Start typing a venue name…"
              onChange={handleInputChange}
              InputProps={{ startAdornment: <LocationOn color="action" sx={{ mr: 1 }} /> }}
            />
          </Autocomplete>
        )}

        {error && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {draft && (
          <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>{draft.name}</Typography>
            <Typography variant="body2" color="text.secondary">{draft.address}</Typography>
            {draft.phone && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{draft.phone}</Typography>
            )}
            {draft.website && (
              <Typography variant="body2" color="text.secondary">{draft.website}</Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Type: {draft.businessType}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!draft || saving || !mapsLoaded || !!error}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Adding…' : 'Add venue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddVenueFromPlacesDialog;
